import { useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { Lock, Eye, EyeOff, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { resetPassword } from '@/api/http/auth-api'
import { extractErrorMessage } from '@/api/http/errors'

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [confirmError, setConfirmError] = useState('')

  if (!token) {
    return (
      <div className="flex items-center justify-center min-h-dvh p-4">
        <div className="rounded-xl border border-border bg-surface p-6 max-w-sm w-full text-center space-y-4">
          <Lock className="w-12 h-12 text-orange mx-auto" strokeWidth={1.5} />
          <h1 className="text-lg font-semibold text-text">Недействительная ссылка</h1>
          <p className="text-sm text-text-muted">
            Ссылка для сброса пароля не содержит токен. Проверьте ссылку из письма.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-accent hover:text-accent/70 transition-colors"
          >
            Вернуться ко входу
          </Link>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setPasswordError('')
    setConfirmError('')

    if (password.length < 6) {
      setPasswordError('Минимум 6 символов')
      return
    }
    if (password !== confirmPassword) {
      setConfirmError('Пароли не совпадают')
      return
    }

    setLoading(true)
    try {
      await resetPassword(token, password)
      setDone(true)
    } catch (err) {
      setError(extractErrorMessage(err, 'Ссылка устарела или недействительна'))
    } finally {
      setLoading(false)
    }
  }

  if (done) {
    return (
      <div className="flex items-center justify-center min-h-dvh p-4">
        <div className="rounded-xl border border-border bg-surface p-6 max-w-sm w-full text-center space-y-4">
          <CheckCircle className="w-12 h-12 text-green mx-auto" strokeWidth={1.5} />
          <h1 className="text-lg font-semibold text-text">Пароль изменён</h1>
          <p className="text-sm text-text-muted">
            Теперь вы можете войти с новым паролем.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-accent hover:text-accent/70 transition-colors"
          >
            Вернуться ко входу
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-dvh p-4">
      <div className="w-full max-w-sm mx-auto">
        <div className="flex flex-col items-center mb-8">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-[#8aa4ff] text-white text-base font-bold mb-4">
            DH
          </div>
          <h1 className="text-xl font-semibold text-text">Новый пароль</h1>
        </div>

        <div className="rounded-xl border border-border bg-surface p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-text">
                Новый пароль <span className="text-red">*</span>
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Минимум 6 символов"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setPasswordError('') }}
                  aria-invalid={!!passwordError}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-text cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={2} /> : <Eye className="w-4 h-4" strokeWidth={2} />}
                </button>
              </div>
              {passwordError && (
                <p className="text-xs text-red mt-1">{passwordError}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-text">
                Подтверждение пароля <span className="text-red">*</span>
              </label>
              <div className="relative">
                <Input
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Повторите пароль"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setConfirmError('') }}
                  aria-invalid={!!confirmError}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-text cursor-pointer"
                  onClick={() => setShowConfirm(!showConfirm)}
                  tabIndex={-1}
                  aria-label={showConfirm ? 'Скрыть пароль' : 'Показать пароль'}
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" strokeWidth={2} /> : <Eye className="w-4 h-4" strokeWidth={2} />}
                </button>
              </div>
              {confirmError && (
                <p className="text-xs text-red mt-1">{confirmError}</p>
              )}
            </div>

            {error && (
              <p className="text-sm text-red text-center">{error}</p>
            )}

            <Button type="submit" className="w-full gap-2" size="lg" disabled={loading}>
              <Lock className="w-4 h-4" strokeWidth={2} />
              {loading ? 'Сохранение...' : 'Сбросить пароль'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}
